//https://stackoverflow.com/questions/41030361/how-to-update-react-context-from-inside-a-child-component

import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useUserSession } from "./UserContext";
import {
  DEFAULT_THEME,
  readCachedUserPreferences,
} from "../helpers/userPreferences";

interface DarkModeContextProps {
  isDarkMode: boolean;
  setDarkMode: (enable: boolean) => void;
}

const DarkModeContext = createContext<DarkModeContextProps>(undefined!);

export function DarkModeProvider({ children }: PropsWithChildren) {
  const { userSession, updateUserPreferences } = useUserSession();
  const [darkMode, setDarkModeState] = useState(
    readCachedUserPreferences().theme === "dark",
  );

  useEffect(() => {
    const profileTheme = userSession?.user.preferences?.theme;
    if (profileTheme) {
      setDarkModeState(profileTheme === "dark");
    }
  }, [userSession?.user.preferences?.theme]);

  // Save latest state to localStorage
  useEffect(() => {
    window.localStorage.setItem("darkMode", darkMode.toString());
  }, [darkMode]);

  const setDarkMode = (enable: boolean) => {
    setDarkModeState(enable);
    void updateUserPreferences({
      theme: enable ? "dark" : DEFAULT_THEME,
    });
  };

  return (
    <DarkModeContext.Provider
      value={{
        isDarkMode: darkMode,
        setDarkMode: setDarkMode,
      }}
    >
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode(): DarkModeContextProps {
  const context = useContext(DarkModeContext);

  if (typeof context === "undefined") {
    throw new Error(
      "useDarkMode should be used within the DarkModeContext provider!",
    );
  }

  return context;
}
